/**
 * Permission Table Component
 * Phase 5: RBAC & Permission System - M3
 * 
 * Custom table component for permission list
 * Pattern: Similar to UserTable (consistent with project standards)
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

const PermissionTable = ({
  permissions,
  total,
  page,
  limit,
  loading,
  onEdit,
  onDelete,
  onPageChange,
  onRowsPerPageChange
}) => {
  // Handle page change
  const handleChangePage = (event, newPage) => {
    onPageChange(newPage + 1); // API uses 1-based pagination
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    onRowsPerPageChange(parseInt(event.target.value, 10));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!permissions || permissions.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Henüz yetki bulunamadı.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Kod</TableCell>
              <TableCell>İsim</TableCell>
              <TableCell align="center">Tip</TableCell>
              <TableCell align="center">Ana Menü</TableCell>
              <TableCell>Yol</TableCell>
              <TableCell align="center">Sıra</TableCell>
              <TableCell align="center">Durum</TableCell>
              <TableCell align="center">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {permissions.map((permission) => (
              <TableRow key={permission._id} hover>
                {/* Code */}
                <TableCell>
                  <Typography 
                    variant="body2" 
                    fontFamily="monospace" 
                    fontSize="13px"
                    sx={{ fontWeight: 500 }}
                  >
                    {permission.code}
                  </Typography>
                </TableCell>

                {/* Name */}
                <TableCell>
                  <Typography variant="body2">
                    {permission.name}
                  </Typography>
                  {permission.description && (
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {permission.description}
                    </Typography>
                  )}
                </TableCell>

                {/* Type (isPage + isMainMenu) */}
                <TableCell align="center">
                  {permission.isPage && permission.isMainMenu ? (
                    <Chip 
                      label="📄 Sayfa" 
                      color="primary" 
                      size="small" 
                      sx={{ minWidth: 100 }}
                    />
                  ) : permission.isPage && !permission.isMainMenu ? (
                    <Chip 
                      label="📄 Sayfa" 
                      color="info" 
                      size="small" 
                      variant="outlined"
                      sx={{ minWidth: 100 }}
                    />
                  ) : !permission.isPage && permission.isMainMenu ? (
                    <Chip 
                      label="📁 Kategori" 
                      color="secondary" 
                      size="small" 
                      sx={{ minWidth: 100 }}
                    />
                  ) : (
                    <Chip 
                      label="⚡ Aksiyon" 
                      color="default" 
                      size="small" 
                      sx={{ minWidth: 100 }}
                    />
                  )}
                </TableCell>

                {/* Main Menu (isMainMenu) */}
                <TableCell align="center">
                  {permission.isMainMenu ? (
                    <Chip 
                      label="Evet" 
                      color="success" 
                      size="small" 
                      icon={<CheckIcon />}
                      sx={{ minWidth: 70 }}
                    />
                  ) : (
                    <Chip 
                      label="Hayır" 
                      color="default" 
                      size="small" 
                      variant="outlined"
                      icon={<CloseIcon />}
                      sx={{ minWidth: 70 }}
                    />
                  )}
                </TableCell>

                {/* Path */}
                <TableCell>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    fontFamily="monospace"
                    fontSize="12px"
                  >
                    {permission.path || '-'}
                  </Typography>
                </TableCell>

                {/* Order */}
                <TableCell align="center">
                  <Typography variant="body2">
                    {permission.order}
                  </Typography>
                </TableCell>

                {/* Status */}
                <TableCell align="center">
                  <Chip 
                    label={permission.isActive ? 'Aktif' : 'Pasif'}
                    color={permission.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>

                {/* Actions */}
                <TableCell align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => onEdit(permission)}
                      title="Düzenle"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onDelete(permission)}
                      title="Sil"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={total}
        page={page - 1} // MUI uses 0-based pagination
        onPageChange={handleChangePage}
        rowsPerPage={limit}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Sayfa başına:"
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} / ${count !== -1 ? count : `${to}'den fazla`}`
        }
      />
    </Paper>
  );
};

PermissionTable.propTypes = {
  permissions: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired
};

export default PermissionTable;

